import React, { useEffect, useState } from 'react';
import { Box, Typography } from "@mui/material";
import SubjectActionItem from './SubjectActionItem';

/**
 * Component to render the Recommended Actions section in the dashboard
 */
const RecommendedActions = ({
  overallStats,
  topSubjects,
  subjectAttributes,
  view,
  isSubjectHighlighted,
  isAttributeHighlightingParent,
  handleSubjectClick,
  handleAttributeClick,
  formatCurrency,
  formatImpact,
  capitalizeSubject,
  capitalizeAttribute,
  getSubjectImpactAction,
  selectedCategory,
  reviewsData,
  sortOption
}) => {
  const [filteredSubjects, setFilteredSubjects] = useState([]);

  // Filter the subjects based on the selected category and sort option
  useEffect(() => {
    if (!selectedCategory || !reviewsData) {
      // If no category is selected, use the overallStats.topSubjects which are already filtered by sortOption
      let subjects = [...topSubjects];
      
      // Apply revenue impact calculation for Sales view even when no category is selected
      if (view === "Sales" && overallStats.positiveRevenueImpact) {
        const multiplier = overallStats.positiveRevenueImpact / overallStats.positiveImpact || 0;
        
        subjects = subjects.map(subject => ({
          ...subject,
          revenueImpact: subject.impact * multiplier
        }));
      }
      
      setFilteredSubjects(subjects);
      return;
    }

    // When a category is selected, we need to:
    // 1. Find all subjects in this category
    // 2. Calculate their impact values
    // 3. Sort them and show the top ones

    // Track subjects and their impact values
    const subjectImpact = {};
    // Track attributes for each subject
    const subjectToAttributes = {};
    
    // Process review data to find subjects in the selected category
    reviewsData.forEach(review => {
      if (!review.analysis || review.analysis.length === 0) return;
      
      review.analysis.forEach(item => {
        const { subject_group, attribute, attribute_impact, sentiment, category } = item;
        // Only process items that match the selected category
        if (category && category.toLowerCase() === selectedCategory.toLowerCase() && subject_group) {
          // Initialize this subject if we haven't seen it yet
          if (!subjectImpact[subject_group]) {
            subjectImpact[subject_group] = { positive: 0, negative: 0, total: 0 };
            subjectToAttributes[subject_group] = { positive: {}, negative: {} };
          }
          
          // Add this impact to the appropriate bucket
          if (attribute_impact > 0) {
            subjectImpact[subject_group].positive += attribute_impact;
            
            // Also track attribute impact
            if (attribute) {
              if (!subjectToAttributes[subject_group].positive[attribute]) {
                subjectToAttributes[subject_group].positive[attribute] = 0;
              }
              subjectToAttributes[subject_group].positive[attribute] += attribute_impact;
            }
          } else if (attribute_impact < 0) {
            subjectImpact[subject_group].negative += attribute_impact;
            
            // Also track attribute impact
            if (attribute) {
              if (!subjectToAttributes[subject_group].negative[attribute]) {
                subjectToAttributes[subject_group].negative[attribute] = 0;
              }
              subjectToAttributes[subject_group].negative[attribute] += Math.abs(attribute_impact);
            }
          }
          
          // Track total impact (absolute value)
          subjectImpact[subject_group].total += Math.abs(attribute_impact);
        }
      });
    });
    
    // Convert to array for sorting
    const subjectsArray = [];
    
    // Create entries based on sort option
    Object.keys(subjectImpact).forEach(subject => {
      if (sortOption === 'positive') {
        // Only add subjects with positive impact for positive sort option
        if (subjectImpact[subject].positive > 0) {
          subjectsArray.push({
            name: subject,
            impact: subjectImpact[subject].positive,
            isPositive: true,
            // Also add the top attributes
            attributes: processAttributes(subjectToAttributes[subject].positive, true)
          });
        }
      } else if (sortOption === 'negative') {
        // Only add subjects with negative impact for negative sort option
        if (subjectImpact[subject].negative < 0) {
          subjectsArray.push({
            name: subject,
            impact: Math.abs(subjectImpact[subject].negative),
            isPositive: false,
            // Also add the top attributes
            attributes: processAttributes(subjectToAttributes[subject].negative, false)
          });
        }
      } else {
        // For 'total' sort option, add both positive and negative
        // Add entry for positive impact if any
        if (subjectImpact[subject].positive > 0) {
          subjectsArray.push({
            name: subject,
            impact: subjectImpact[subject].positive,
            isPositive: true,
            // Also add the top attributes
            attributes: processAttributes(subjectToAttributes[subject].positive, true)
          });
        }
        
        // Add entry for negative impact if any
        if (subjectImpact[subject].negative < 0) {
          subjectsArray.push({
            name: subject,
            impact: Math.abs(subjectImpact[subject].negative),
            isPositive: false,
            // Also add the top attributes
            attributes: processAttributes(subjectToAttributes[subject].negative, false)
          });
        }
      }
    });
    
    // Helper function to process attributes
    function processAttributes(attributesObj, isPositive) {
      const entries = Object.entries(attributesObj);
      // If there are no attributes, return empty array
      if (entries.length === 0) {
        return [];
      }
      
      return entries
        .map(([name, impact]) => ({
          name,
          impact,
          sentiment: isPositive ? 'positive' : 'negative',
          // Calculate revenue impact if needed
          revenueImpact: view === "Sales" && overallStats.positiveRevenueImpact
            ? impact * (overallStats.positiveRevenueImpact / overallStats.positiveImpact || 0)
            : 0
        }))
        .sort((a, b) => b.impact - a.impact)
        .slice(0, 3); // Take top 3 attributes
    }
    
    // Sort by impact (highest first)
    subjectsArray.sort((a, b) => b.impact - a.impact);
    
    // Take top subjects (limit to 3 to match the default view)
    const topFilteredSubjects = subjectsArray.slice(0, 3);
    
    // Calculate revenue impact if applicable
    if (view === "Sales" && overallStats.positiveRevenueImpact) {
      // For the view to be "Sales", the app must have a business with turnover 
      // We can infer the multiplier from the ratio of revenue impact to regular impact
      const multiplier = overallStats.positiveRevenueImpact / overallStats.positiveImpact || 0;
      
      // Apply the multiplier to each subject
      topFilteredSubjects.forEach(subject => {
        subject.revenueImpact = subject.impact * multiplier;
      });
    }
    
    setFilteredSubjects(topFilteredSubjects);
  }, [selectedCategory, reviewsData, view, overallStats.positiveRevenueImpact, overallStats.positiveImpact, sortOption, topSubjects]);

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'auto'
    }}>
      <Box sx={{ mb: 1, mt: '8px' }}>
        <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.7 }}>
          {selectedCategory 
            ? (
                <React.Fragment>
                  Recommended actions for <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '1.125rem' }}>{selectedCategory}</Typography>:
                </React.Fragment>
              )
            : 'Recommended actions based on top impact subjects:'}
        </Typography>
      </Box>

      {filteredSubjects.length > 0 ? (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column'
        }}>
          {filteredSubjects.map((subject, index) => {
            // Use the attributes we already computed, or fall back to the existing logic for non-category filtering
            const attributes = subject.attributes || (() => {
              const subjectKey = `${subject.name}:${subject.isPositive ? 'positive' : 'negative'}`;
              return subjectAttributes[subjectKey] || [];
            })();

            return (
              <SubjectActionItem
                key={index}
                subject={subject}
                attributes={attributes}
                view={view}
                isSubjectHighlighted={isSubjectHighlighted}
                isAttributeHighlightingParent={isAttributeHighlightingParent}
                handleSubjectClick={handleSubjectClick}
                handleAttributeClick={handleAttributeClick}
                formatCurrency={formatCurrency}
                formatImpact={formatImpact}
                capitalizeSubject={capitalizeSubject}
                capitalizeAttribute={capitalizeAttribute}
                getSubjectImpactAction={getSubjectImpactAction}
              />
            );
          })}
        </Box>
      ) : (
        <Box sx={{ color: 'text.secondary' }}>
          <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.7 }}>
            {selectedCategory 
              ? (
                  <React.Fragment>
                    No recommended actions found for <Typography component="span" sx={{ fontWeight: 'bold', fontSize: '1.125rem' }}>{selectedCategory}</Typography>.
                  </React.Fragment>
                )
              : 'No recommended actions available.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RecommendedActions; 